import type { FuseResultMatch } from "fuse.js";
import React from "react";
import styled from "styled-components";

const Link = styled.a`
  color: var(--blue400) !important;

  &:hover {
    text-decoration: underline;
  }
`;

const Highlight = styled.span`
  background-color: var(--blue100);
  color: var(--gray500);
`;

const AutoDecorate = (props: {
  text: string | undefined;
  fuseMatches?: FuseResultMatch[];
}) => {
  const { text, fuseMatches } = props;

  let highlightRanges = fuseMatches?.flatMap(({ indices }) => indices);

  let longestRangeLength = 0;
  highlightRanges?.forEach((range) => {
    const length = range[1] - range[0];
    if (length > longestRangeLength) {
      longestRangeLength = length;
    }
  });

  highlightRanges = highlightRanges?.filter(
    (range) => range[1] - range[0] === longestRangeLength
  );

  // RangeTuples are inclusive
  const highlight = (str: string, firstIndex: number) => {
    const finalIndexInclusive = firstIndex + str.length - 1;

    const elements: React.ReactNode[] = [];
    let lastIndex = firstIndex;
    highlightRanges?.forEach((range) => {
      const isOverlapping =
        range[0] <= finalIndexInclusive && firstIndex <= range[1];
      if (isOverlapping) {
        // clipped Range out index is exclusive
        const clippedRange = [
          Math.max(range[0], lastIndex),
          Math.min(range[1], finalIndexInclusive) + 1,
        ];

        if (clippedRange[0] < clippedRange[1]) {
          if (clippedRange[0] > lastIndex) {
            elements.push(
              str.substring(
                lastIndex - firstIndex,
                clippedRange[0] - firstIndex
              )
            );
          }

          const highlightedText = str.substring(
            clippedRange[0] - firstIndex,
            clippedRange[1] - firstIndex
          );
          elements.push(
            <Highlight key={clippedRange.toString()}>
              {highlightedText}
            </Highlight>
          );

          lastIndex = clippedRange[1];
        }
      }
    });

    if (str.length > lastIndex - firstIndex) {
      elements.push(str.substring(lastIndex - firstIndex));
    }

    return elements;
  };

  const parseString = (str?: string) => {
    if (!str) {
      return str;
    }

    const matches = str.matchAll(
      /https?:\/\/[\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-]/gi
    );

    let elements: React.ReactNode[] = [];
    let lastIndex = 0;
    Array.from(matches).forEach((match, i) => {
      const url = match[0];
      const matchIndex = match.index ?? 0;

      // Push preceding text if there is any
      if (matchIndex > lastIndex) {
        elements = [
          ...elements,
          ...highlight(str.substring(lastIndex, matchIndex), lastIndex),
        ];
      }

      elements.push(
        <Link
          href={url}
          target="_blank"
          rel="noreferrer"
          key={i}
          onClick={(e) => e.stopPropagation()}
        >
          {highlight(url, matchIndex)}
        </Link>
      );

      lastIndex = matchIndex + url.length;
    });

    // Push remaining text if there is any
    if (str.length > lastIndex) {
      elements = [
        ...elements,
        ...highlight(str.substring(lastIndex), lastIndex),
      ];
    }

    return elements.length === 1 ? elements[0] : elements;
  };

  return <>{parseString(text)}</>;
};

export default AutoDecorate;
